<template>
  <v-container fluid class="sge">
    <!-- Header -->
    <header class="sge__head">
      <div class="sge__title">
        <v-icon size="22" class="mr-2">mdi-transmission-tower</v-icon>
        <h1>{{ $filters.capitalize($t("sge.title")) }}</h1>
        <span class="sge__meta">
          <template v-if="sending">
            <v-progress-circular indeterminate size="12" width="2" class="mx-1"></v-progress-circular>
            {{ $t("sge.sending_hint") }}
          </template>
        </span>
      </div>

      <div class="tools">
        <v-text-field
          v-model="search"
          density="compact"
          variant="solo"
          flat
          rounded="pill"
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="$filters.capitalize($t('search'))"
          class="tools__search"
          bg-color="transparent"
        ></v-text-field>

        <span class="tools__sep"></span>

        <label class="tools__auto" :class="{ 'tools__auto--on': autoRefresh }">
          <v-switch
            v-model="autoRefresh"
            density="compact"
            inset
            hide-details
            color="primary"
            class="tools__switch"
          ></v-switch>
          <span class="tools__auto-text">
            <span>{{ $t("sge.auto_refresh") }}</span>
            <span class="tools__auto-sub">{{ autoRefresh ? $t("sge.auto_every", { s: 60 }) : $t("sge.auto_off") }}</span>
          </span>
        </label>

        <span class="tools__sep"></span>

        <v-btn
          variant="tonal"
          color="primary"
          rounded="pill"
          size="small"
          class="tools__refresh sge-btn"
          prepend-icon="mdi-refresh"
          :loading="loading"
          @click="refresh"
        >
          {{ $filters.capitalize($t("sge.refresh")) }}
          <span v-if="lastRefresh" class="tools__time">{{ lastRefresh }}</span>
        </v-btn>
      </div>
    </header>

    <!-- Fleet state: the one thing to read first -->
    <section class="fleet" :aria-label="$t('sge.fleet_label')">
      <div class="fleet__bar" role="img" :aria-label="fleetAria">
        <div v-if="summary.stopped" class="fleet__seg fleet__seg--stop" :style="{ flexGrow: summary.stopped }"></div>
        <div v-if="summary.released" class="fleet__seg fleet__seg--run" :style="{ flexGrow: summary.released }"></div>
        <div v-if="summary.unknown" class="fleet__seg fleet__seg--none" :style="{ flexGrow: summary.unknown }"></div>
      </div>
      <div class="fleet__row">
        <div class="fleet__legend">
          <span class="fleet__item"><b>{{ summary.total }}</b> {{ $t("sge.sum_total") }}</span>
          <span class="fleet__item state state--stop"><i class="state__dot"></i><b>{{ summary.stopped }}</b> {{ $t("sge.sum_stopped") }}</span>
          <span class="fleet__item state state--run"><i class="state__dot"></i><b>{{ summary.released }}</b> {{ $t("sge.sum_released") }}</span>
          <span v-if="summary.unknown" class="fleet__item state state--none"><i class="state__dot"></i><b>{{ summary.unknown }}</b> {{ $t("sge.sum_unknown") }}</span>
        </div>
        <div class="fleet__alerts">
          <span v-if="summary.plcDown" class="alert alert--bad"><v-icon size="15">mdi-lan-disconnect</v-icon><b>{{ summary.plcDown }}</b> {{ $t("sge.sum_plc_down") }}</span>
          <span v-if="summary.mismatch" class="alert alert--warn"><v-icon size="15">mdi-alert-outline</v-icon><b>{{ summary.mismatch }}</b> {{ $t("sge.sum_mismatch") }}</span>
          <span v-if="summary.noData" class="alert alert--warn"><v-icon size="15">mdi-help-circle-outline</v-icon><b>{{ summary.noData }}</b> {{ $t("sge.sum_no_data") }}</span>
          <span v-if="!summary.plcDown && !summary.mismatch && !summary.noData && summary.total" class="alert alert--ok"><v-icon size="15">mdi-check</v-icon>{{ $t("sge.sum_all_good") }}</span>
        </div>
      </div>
    </section>

    <!-- Batch actions on the selection -->
    <div class="batch" :class="{ 'batch--active': selected.length }">
      <span class="batch__count">
        <template v-if="selected.length">{{ $t("sge.selected_n", { n: selected.length }) }}</template>
        <template v-else>{{ $t("sge.select_hint") }}</template>
      </span>
      <v-btn
        color="#0a63a8"
        variant="flat"
        size="small"
        class="sge-btn mr-2"
        prepend-icon="mdi-stop-circle"
        :disabled="sending || selected.length === 0"
        @click="openConfirm(true, selected)"
      >
        {{ $t("sge.stop_order") }}
      </v-btn>
      <v-tooltip location="top" max-width="300" content-class="sge-tip">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            color="green-darken-2"
            variant="flat"
            size="small"
            class="sge-btn"
            prepend-icon="mdi-play-circle"
            :disabled="sending || selected.length === 0"
            @click="openConfirm(false, selected)"
          >
            {{ $t("sge.cancel_order") }}
          </v-btn>
        </template>
        <strong>{{ $t("sge.cancel_order") }}</strong> · {{ $t("sge.start_tooltip") }}
      </v-tooltip>
    </div>

    <!-- Producers table -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="producers"
      :search="search"
      :loading="loading"
      item-value="site_id"
      show-select
      density="compact"
      :items-per-page="-1"
      hide-default-footer
      :sort-by="[{ key: 'name', order: 'asc' }]"
      :row-props="rowProps"
      class="sge-table"
    >
      <template v-slot:item.name="{ item }">
        <div class="site">
          <span class="site__name">{{ item.name }}</span>
          <span class="site__id">{{ item.site_id }}</span>
        </div>
      </template>

      <template v-slot:item.actual_prod="{ item }">
        <span v-if="item.p_active_kW === null || item.p_active_kW === undefined" class="state state--bad" :title="$t('sge.no_power_data_hint')">
          <i class="state__dot"></i>{{ $t("sge.power_data_missing") }}
        </span>
        <span v-else class="state state--run"><i class="state__dot"></i>{{ $t("sge.power_data_ok") }}</span>
      </template>

      <template v-slot:item.p_active_kW="{ item }">
        <span class="num">{{ item.p_active_kW !== null && item.p_active_kW !== undefined ? $filters.formatFloat(item.p_active_kW) : "—" }}</span>
      </template>

      <template v-slot:item.sge_order="{ item }">
        <v-tooltip location="top" max-width="320" content-class="sge-tip">
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="state state--tip" :class="stateClass(item.sge_order)">
              <i class="state__dot"></i>{{ stateLabel(item.sge_order) }}
            </span>
          </template>
          <strong>{{ stateLabel(item.sge_order) }}</strong> ·
          {{ item.sge_order === true ? $t("sge.tip_influx_stop") : item.sge_order === false ? $t("sge.tip_influx_start") : $t("sge.tip_influx_none") }}
          <template v-if="item.sge_order_time"><br />{{ $t("sge.tip_sent_at", { d: formatDate(item.sge_order_time) }) }}</template>
        </v-tooltip>
        <div v-if="item.sge_order_time" class="sub">{{ formatDate(item.sge_order_time) }}</div>
      </template>

      <template v-slot:item.p_limit_sge="{ item }">
        <v-tooltip location="top" max-width="320" content-class="sge-tip">
          <template v-slot:activator="{ props }">
            <span
              v-bind="props"
              class="state state--tip"
              :class="[stateClass(plcLimit(item)), plcMismatch(item) ? 'state--warn' : '']"
            >
              <i class="state__dot"></i>{{ stateLabel(plcLimit(item)) }}
              <v-icon v-if="plcMismatch(item)" size="14" class="ml-1">mdi-alert-outline</v-icon>
            </span>
          </template>
          <strong>{{ stateLabel(plcLimit(item)) }}</strong> ·
          {{ plcLimit(item) === true ? $t("sge.tip_plc_stop") : plcLimit(item) === false ? $t("sge.tip_plc_start") : $t("sge.tip_plc_none") }}
          <template v-if="plcMismatch(item)"><br /><strong>{{ $t("sge.plc_mismatch") }}</strong></template>
        </v-tooltip>
      </template>

      <template v-slot:item.sge_target_skW="{ item }">
        <span class="num">{{ item.sge_target_skW !== null && item.sge_target_skW !== undefined ? $filters.formatFloat(item.sge_target_skW) : "—" }}</span>
      </template>

      <template v-slot:item.set_point_sge="{ item }">
        <span class="num" :class="plcTargetMismatch(item) ? 'num--warn' : ''" :title="plcTargetMismatch(item) ? $t('sge.plc_mismatch') : ''">
          {{ item.set_point_sge !== null && item.set_point_sge !== undefined ? $filters.formatFloat(item.set_point_sge) : "—" }}
        </span>
      </template>

      <template v-slot:item.plc_latency_ms="{ item }">
        <span v-if="item.plc_connected === null || item.plc_connected === undefined" class="state state--none"><i class="state__dot"></i>—</span>
        <span v-else class="state" :class="isPlcDown(item) ? 'state--bad' : latencyClass(item)" :title="item.plc_error || ''">
          <i class="state__dot"></i>
          <span class="num">{{ isPlcDown(item) ? $t("sge.plc_down") : item.plc_latency_ms + " ms" }}</span>
        </span>
      </template>

      <template v-slot:item.telemetry_time="{ item }">
        <span class="num" :class="item.telemetry_ok ? '' : 'num--bad'">
          {{ item.telemetry_time ? formatDate(item.telemetry_time) : $t("sge.no_data") }}
        </span>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="row-actions">
          <v-btn
            size="small"
            color="#0a63a8"
            :variant="item.sge_order === true ? 'outlined' : 'flat'"
            class="sge-btn sge-row-btn"
            prepend-icon="mdi-stop-circle"
            :loading="pending[item.site_id] === true"
            :disabled="sending"
            :title="item.sge_order === true ? $t('sge.row_stop_again') : $t('sge.row_stop_hint')"
            @click="openConfirm(true, [item.site_id])"
          >
            {{ $t("sge.row_stop") }}
          </v-btn>
          <v-tooltip location="top" max-width="300" content-class="sge-tip">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                size="small"
                color="green-darken-2"
                :variant="item.sge_order === false ? 'outlined' : 'flat'"
                class="sge-btn sge-row-btn"
                prepend-icon="mdi-play-circle"
                :loading="pending[item.site_id] === false"
                :disabled="sending"
                @click="openConfirm(false, [item.site_id])"
              >
                {{ $t("sge.row_release") }}
              </v-btn>
            </template>
            <strong>{{ $t("sge.row_release") }}</strong> · {{ item.sge_order === false ? $t("sge.row_release_again") : $t("sge.start_tooltip") }}
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <!-- Confirmation dialog -->
    <v-dialog v-model="confirmDialog" max-width="560" scrollable>
      <v-card class="confirm" :class="pendingOrder ? 'confirm--stop' : 'confirm--start'">
        <div class="confirm__band">
          <v-icon size="30" class="confirm__icon">{{ pendingOrder ? "mdi-stop-circle" : "mdi-play-circle" }}</v-icon>
          <div>
            <div class="confirm__kicker">{{ $t("sge.confirm_kicker") }}</div>
            <h2 class="confirm__title">{{ pendingOrder ? $t("sge.confirm_stop_title") : $t("sge.confirm_cancel_title") }}</h2>
            <div class="confirm__sub">{{ $t("sge.confirm_sites_n", { n: pendingSites.length }) }}</div>
          </div>
        </div>

        <v-card-text class="confirm__body">
          <div class="confirm__sites">
            <span
              v-for="s in pendingSites"
              :key="s"
              class="confirm__site"
              :class="{ 'confirm__site--raised': pendingOrder && siteMinKw(s) && effectiveStopKw(s) !== Math.max(0, Number(pendingLimitKw) || 0) }"
            >
              {{ siteName(s) }}
              <span v-if="pendingOrder && siteMinKw(s)" class="confirm__site-min">
                {{ $t("sge.min_kw", { kw: siteMinKw(s) }) }}
              </span>
            </span>
          </div>

          <div v-if="pendingOrder" class="limit">
            <div class="limit__row">
              <div class="limit__text">
                <div class="limit__label">
                  <v-icon size="18" class="mr-1">mdi-speedometer-slow</v-icon>
                  {{ $t("sge.limit_label") }}
                </div>
                <div class="limit__hint">{{ $t("sge.limit_hint") }}</div>
              </div>
              <v-text-field
                v-model.number="pendingLimitKw"
                type="number"
                min="0"
                step="10"
                suffix="kW"
                density="compact"
                variant="outlined"
                hide-details
                class="limit__field"
              ></v-text-field>
            </div>
            <div class="limit__info">
              <v-icon size="16" class="mr-1">mdi-information-outline</v-icon>
              <span>{{ $t("sge.limit_min_info") }}</span>
            </div>
          </div>

          <div v-if="pendingOrder" class="option" :class="{ 'option--on': pendingInstant }">
            <div class="option__text">
              <div class="option__label">
                <v-icon size="18" class="mr-1">mdi-flash</v-icon>
                {{ $t("sge.instant_label") }}
              </div>
              <div class="option__hint">
                {{ pendingInstant ? $t("sge.instant_hint_on") : $t("sge.instant_hint_off") }}
              </div>
            </div>
            <v-switch
              v-model="pendingInstant"
              inset
              hide-details
              density="compact"
              :color="pendingOrder ? '#0a63a8' : 'green-darken-2'"
              class="option__switch"
            ></v-switch>
          </div>

          <p class="confirm__note">
            <v-icon size="15" class="mr-1">mdi-email-outline</v-icon>{{ $t("sge.confirm_text", { n: pendingSites.length }) }}
          </p>
        </v-card-text>

        <v-card-actions class="confirm__actions">
          <v-btn variant="text" class="sge-btn" @click="confirmDialog = false">{{ $filters.capitalize($t("cancel")) }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :color="pendingOrder ? '#0a63a8' : 'green-darken-2'"
            variant="flat"
            size="large"
            class="sge-btn confirm__send"
            :prepend-icon="pendingOrder ? 'mdi-stop-circle' : 'mdi-play-circle'"
            :disabled="pendingSites.length === 0"
            @click="sendOrders"
          >
            {{ pendingOrder ? $t("sge.send_stop") : $t("sge.send_start") }} ({{ pendingSites.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Results dialog -->
    <v-dialog v-model="resultDialog" max-width="640" scrollable>
      <v-card class="results" :class="allOk ? 'results--ok' : 'results--ko'">
        <div class="results__band">
          <v-icon size="30" class="results__icon">{{ allOk ? "mdi-check-circle" : "mdi-alert-circle" }}</v-icon>
          <div>
            <div class="results__kicker">{{ resultOrder ? $t("sge.stop_order") : $t("sge.cancel_order") }} · {{ $t("sge.results_title") }}</div>
            <h2 class="results__title">
              {{ allOk ? $t("sge.results_all_ok", { n: results.length }) : $t("sge.results_some_ko", { ko: failedCount, n: results.length }) }}
            </h2>
            <div class="results__sub">{{ $t("sge.results_hint") }}</div>
          </div>
        </div>

        <v-card-text class="results__body">
          <ul class="rlist">
            <li v-for="r in results" :key="r.site_id" class="rlist__row" :class="r.ok ? 'rlist__row--ok' : 'rlist__row--ko'">
              <v-icon size="18" class="rlist__icon">{{ r.ok ? "mdi-check-circle" : "mdi-close-circle" }}</v-icon>
              <div class="rlist__main">
                <div class="rlist__site">{{ siteName(r.site_id) }}</div>
                <div v-if="!r.ok" class="rlist__err">{{ r.response || $t("sge.results_no_reply") }}</div>
              </div>
              <div class="rlist__meta">
                <span v-if="r.applied_kw !== null && r.applied_kw !== undefined" class="rlist__kw num">{{ r.applied_kw }} kW</span>
                <span class="rlist__code num" :title="$t('sge.col_status')">{{ r.status_code ?? "—" }}</span>
                <span class="rlist__ms num">{{ r.elapsed_ms !== null && r.elapsed_ms !== undefined ? r.elapsed_ms + " ms" : "—" }}</span>
              </div>
            </li>
          </ul>
        </v-card-text>

        <v-card-actions class="results__actions">
          <v-btn
            v-if="!allOk"
            variant="tonal"
            :color="resultOrder ? '#0a63a8' : 'green-darken-2'"
            class="sge-btn"
            prepend-icon="mdi-refresh"
            :disabled="sending"
            @click="retryFailed"
          >
            {{ $t("sge.results_retry", { n: failedCount }) }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="flat" color="primary" class="sge-btn" @click="resultDialog = false">{{ $filters.capitalize($t("close")) }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";

import { api } from "@/services/api";
import { fetchSgeProducers, isSgeUser, sendSgeSetPoint } from "@/services/sge";

const REFRESH_INTERVAL_MS = 60000;
const MAX_PARALLEL_ORDERS = 8;
const POST_ACTION_REFRESH_DELAYS_MS = [5000, 15000, 40000];

export default {
  name: "SgeView",

  data() {
    return {
      producers: [],
      selected: [],
      search: "",
      loading: false,
      lastRefresh: null,
      autoRefresh: true,
      intervalId: null,
      followUpTimers: [],

      confirmDialog: false,
      pendingOrder: true,
      pendingSites: [],
      pendingInstant: true, // Stop only: true = PLC applies at once; false = temporary phase first (10 min). Start is always instant.
      pendingLimitKw: 0, // Stop only: production still allowed (kW). The API raises it to the site's p_min.

      sending: false,
      pending: {}, // site_id -> sge_order currently being sent
      results: [],
      resultOrder: true, // order the results belong to
      allOk: false,
      resultDialog: false,
    };
  },

  computed: {
    isSgeUser() {
      return isSgeUser(this.$store.state.user);
    },
    headers() {
      return [
        { title: this.$t("sge.col_site"), key: "name", sortable: true },
        { title: this.$t("sge.col_actual_prod"), key: "actual_prod", sortable: true },
        { title: this.$t("sge.col_power"), key: "p_active_kW", sortable: true, align: "end" },
        {
          title: this.$t("sge.col_limitation_group"),
          align: "center",
          children: [
            { title: this.$t("sge.col_src_influx"), key: "sge_order", sortable: true },
            { title: this.$t("sge.col_src_plc"), key: "p_limit_sge", sortable: true },
          ],
        },
        {
          title: this.$t("sge.col_target_group"),
          align: "center",
          children: [
            { title: this.$t("sge.col_src_influx"), key: "sge_target_skW", sortable: true, align: "end" },
            { title: this.$t("sge.col_src_plc"), key: "set_point_sge", sortable: true, align: "end" },
          ],
        },
        { title: this.$t("sge.col_plc_link"), key: "plc_latency_ms", sortable: true },
        { title: this.$t("sge.col_last_data"), key: "telemetry_time", sortable: true },
        { title: this.$t("actions.label"), key: "actions", sortable: false, align: "end", width: 250 },
      ];
    },
    /** Fleet counters feeding the state bar. */
    summary() {
      const sum = { total: 0, stopped: 0, released: 0, unknown: 0, plcDown: 0, noData: 0, mismatch: 0 };
      for (const p of this.producers) {
        sum.total += 1;
        if (p.sge_order === true) sum.stopped += 1;
        else if (p.sge_order === false) sum.released += 1;
        else sum.unknown += 1;
        if (this.isPlcDown(p)) sum.plcDown += 1;
        if (p.p_active_kW === null || p.p_active_kW === undefined) sum.noData += 1;
        if (this.plcMismatch(p) || this.plcTargetMismatch(p)) sum.mismatch += 1;
      }
      return sum;
    },
    failedCount() {
      return this.results.filter((r) => !r.ok).length;
    },
    fleetAria() {
      const s = this.summary;
      return `${s.stopped} ${this.$t("sge.sum_stopped")}, ${s.released} ${this.$t("sge.sum_released")}, ${s.unknown} ${this.$t("sge.sum_unknown")}`;
    },
  },

  watch: {
    autoRefresh(on) {
      if (on) this.startAutoRefresh();
      else this.stopAutoRefresh();
    },
  },

  methods: {
    stateClass(v) {
      if (v === true) return "state--stop";
      if (v === false) return "state--run";
      return "state--none";
    },
    stateLabel(v) {
      if (v === true) return this.$t("sge.state_stopped");
      if (v === false) return this.$t("sge.state_released");
      return "—";
    },
    latencyClass(item) {
      const ms = item.plc_latency_ms;
      if (ms === null || ms === undefined) return "state--none";
      if (ms > 2000) return "state--bad";
      if (ms > 500) return "state--warn";
      return "state--run";
    },
    /** PLC limitation flag (P_limit_SGE) as a boolean, null when unknown. */
    plcLimit(item) {
      const v = item.p_limit_sge;
      if (v === null || v === undefined || v === "") return null;
      if (typeof v === "boolean") return v;
      if (typeof v === "string") return ["true", "1", "on"].includes(v.toLowerCase());
      return Number(v) !== 0;
    },
    /** True when the PLC limitation flag disagrees with the last order written in Influx. */
    plcMismatch(item) {
      const plc = this.plcLimit(item);
      if (plc === null || item.sge_order === null || item.sge_order === undefined) return false;
      return plc !== item.sge_order;
    },
    /** True when the PLC set point differs from the last target written in Influx. */
    plcTargetMismatch(item) {
      const a = item.set_point_sge, b = item.sge_target_skW;
      if (a === null || a === undefined || b === null || b === undefined) return false;
      return Math.abs(Number(a) - Number(b)) > 0.5;
    },
    /** True when the PLC worker is not reachable: disconnected flag, or a 0 ms latency (never answered). */
    isPlcDown(item) {
      return item.plc_connected === false || item.plc_latency_ms === 0;
    },
    rowProps({ item }) {
      if (this.isPlcDown(item)) return { class: "sge-plc-down-row" };
      if (item.sge_order === true) return { class: "sge-stop-row" };
      return {};
    },
    formatDate(value) {
      return moment(value).format("DD/MM HH:mm");
    },

    siteName(siteId) {
      const p = this.producers.find((x) => x.site_id === siteId);
      return p ? p.name : siteId;
    },
    /** Technical minimum enforced by the API for this site (kW), 0 when none. */
    siteMinKw(siteId) {
      const p = this.producers.find((x) => x.site_id === siteId);
      return p && p.p_min ? Number(p.p_min) : 0;
    },
    /** What the API will really apply for a Stop on this site, given the requested limit. */
    effectiveStopKw(siteId) {
      const asked = Math.max(0, Number(this.pendingLimitKw) || 0);
      const min = this.siteMinKw(siteId);
      return min && asked < min ? min : asked;
    },

    async refresh({ force = false } = {}) {
      if (!this.isSgeUser) return;
      if (this.loading && !force) return;
      this.loading = true;
      try {
        this.producers = await fetchSgeProducers();
        this.lastRefresh = moment().format("HH:mm:ss");
      } catch (err) {
        // error already shown by the sgeApi interceptor
      } finally {
        this.loading = false;
      }
    },

    /**
     * Right after an order: refresh now, then again a few seconds later so the
     * PLC columns (read by the worker on its next tick) catch up with the Influx order.
     */
    refreshAfterAction() {
      this.clearFollowUps();
      this.refresh({ force: true });
      this.followUpTimers = POST_ACTION_REFRESH_DELAYS_MS.map((ms) =>
        window.setTimeout(() => this.refresh({ force: true }), ms)
      );
    },

    clearFollowUps() {
      (this.followUpTimers || []).forEach((t) => window.clearTimeout(t));
      this.followUpTimers = [];
    },

    startAutoRefresh() {
      this.stopAutoRefresh();
      this.intervalId = window.setInterval(() => {
        if (!this.sending) this.refresh();
      }, REFRESH_INTERVAL_MS);
    },

    stopAutoRefresh() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },

    openConfirm(sgeOrder, siteIds) {
      if (!this.isSgeUser) return;
      this.pendingOrder = sgeOrder;
      this.pendingSites = [...siteIds];
      this.pendingInstant = true;
      this.pendingLimitKw = 0;
      this.confirmDialog = true;
    },

    /**
     * Audit + e-mail through raijin. Fire-and-forget: it must never delay the order.
     * raijin itself sends the mail in a background thread.
     */
    notifyRaijin(sgeOrder, siteIds) {
      api
        .post("sge/notify/", { sge_order: sgeOrder, site_ids: siteIds }, { timeout: 15000 })
        .catch(() => {
          /* error already shown by the api interceptor; the order is not affected */
        });
    },

    /** Re-open the confirmation for the sites that failed, keeping the same order and options. */
    retryFailed() {
      const failed = this.results.filter((r) => !r.ok).map((r) => r.site_id);
      if (!failed.length) return;
      const keepInstant = this.pendingInstant;
      const keepLimit = this.pendingLimitKw;
      this.resultDialog = false;
      this.openConfirm(this.resultOrder, failed);
      this.pendingInstant = keepInstant;
      this.pendingLimitKw = keepLimit;
    },

    async sendOrders() {
      this.confirmDialog = false;
      if (!this.isSgeUser || this.pendingSites.length === 0) return;

      const sgeOrder = this.pendingOrder;
      const siteIds = [...this.pendingSites];
      const instant = sgeOrder ? this.pendingInstant : true; // Start is always instant
      const limitKw = sgeOrder ? Math.max(0, Number(this.pendingLimitKw) || 0) : 0;

      this.sending = true;
      this.results = [];
      siteIds.forEach((s) => (this.pending[s] = sgeOrder));

      this.notifyRaijin(sgeOrder, siteIds);

      try {
        // Direct calls to sge_m2m_api, a bounded number at a time.
        const results = [];
        const queue = [...siteIds];
        const worker = async () => {
          while (queue.length) {
            const siteId = queue.shift();
            const r = await sendSgeSetPoint(siteId, sgeOrder, instant, limitKw);
            delete this.pending[siteId];
            results.push(r);
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(MAX_PARALLEL_ORDERS, siteIds.length) }, worker)
        );
        results.sort((a, b) => a.site_id.localeCompare(b.site_id));
        this.results = results;
        this.resultOrder = sgeOrder;
        this.allOk = results.every((r) => r.ok);
        this.resultDialog = true;
      } finally {
        this.pending = {};
        this.sending = false;
        this.refreshAfterAction();
      }
    },
  },

  mounted() {
    if (!this.isSgeUser) {
      this.$store.dispatch("snackbar/showError", this.$filters.capitalize(this.$t("error.forbidden")));
      this.$router.replace({ name: "home" });
      return;
    }
    this.refresh();
    if (this.autoRefresh) this.startAutoRefresh();
  },

  beforeUnmount() {
    this.stopAutoRefresh();
    this.clearFollowUps();
  },
};
</script>

<style scoped>
/* Tokens: ink, states, surfaces. Colour only ever means a state. */
.sge {
  --ink: #1f2a30;
  --ink-2: #5b6770;
  --line: #e3ded4;
  --surface: #ffffff;
  --stop: #b3261e;        /* failures only (PLC down, no data) */
  --halt: #0a63a8;        /* Stop order state: deliberate, not an error */
  --halt-bg: #bfe0f8;
  --halt-bg-strong: #a7d3f3;
  --run: #2e7d32;
  --warn: #b26a00;
  --none: #9aa4ab;
  --stop-bg: #fbeae8;
  color: var(--ink);
  padding: 12px 16px 24px;
}

/* Header */
.sge__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.sge__title { justify-content: center; }
.sge__title {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 12px;
}
.sge__title h1 {
  font-size: 1.6rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0;
}
.sge__meta {
  font-size: 0.8rem;
  color: var(--ink-2);
  display: inline-flex;
  align-items: center;
}
.tools {
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 4px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 999px;
}
.tools__search {
  width: 230px;
}
.tools__search :deep(.v-field) {
  font-size: 0.9rem;
}
.tools__search :deep(.v-field__prepend-inner .v-icon) {
  color: var(--ink-2);
  opacity: 1;
}
.tools__sep {
  width: 1px;
  height: 22px;
  background: var(--line);
  margin: 0 4px;
}
.tools__auto {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 10px 0 2px;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  color: var(--ink-2);
}
.tools__auto--on { color: var(--ink); }
.tools__switch { flex: none; }
.tools__switch :deep(.v-selection-control) { min-height: 0; }
.tools__auto-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
  font-size: 0.82rem;
  font-weight: 500;
}
.tools__auto-sub {
  font-size: 0.7rem;
  font-weight: 400;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
}
.tools__refresh {
  height: 34px;
}
.tools__time {
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid rgba(0, 0, 0, 0.15);
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
}

/* Fleet state bar */
.fleet {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 6px;
  padding: 12px 16px 10px;
  margin-bottom: 12px;
}
.fleet__bar {
  display: flex;
  height: 14px;
  border-radius: 3px;
  overflow: hidden;
  background: var(--line);
  gap: 2px;
}
.fleet__seg {
  flex-basis: 0;
  min-width: 6px;
  transition: flex-grow 300ms ease;
}
@media (prefers-reduced-motion: reduce) {
  .fleet__seg { transition: none; }
}
.fleet__seg--stop { background: var(--halt); }
.fleet__seg--run { background: var(--run); }
.fleet__seg--none { background: var(--none); }
.fleet__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
  margin-top: 10px;
}
.fleet__legend, .fleet__alerts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  font-size: 0.85rem;
}
.fleet__alerts { margin-left: auto; }
.fleet__item b, .alert b {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  margin-right: 0.3em;
}
.alert {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
}
.alert--bad { color: var(--stop); }
.alert--warn { color: var(--warn); }
.alert--ok { color: var(--run); }

/* Batch bar */
.batch {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0 10px;
  font-size: 0.85rem;
  color: var(--ink-2);
}
.batch__count {
  min-width: 190px;
  font-variant-numeric: tabular-nums;
}
.batch--active .batch__count { color: var(--ink); font-weight: 500; }

/* State: dot + word. Same device everywhere a status is shown. */
.state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-weight: 500;
}
.state__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  flex: none;
}
.state--stop { color: var(--halt); }
.state--run { color: var(--run); }
.state--warn { color: var(--warn); }
.state--bad { color: var(--stop); }
.state--none { color: var(--none); font-weight: 400; }
.state--tip { cursor: help; }

.num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.num--warn { color: var(--warn); font-weight: 500; }
.num--bad { color: var(--stop); }
.sub {
  font-size: 0.75rem;
  color: var(--ink-2);
  line-height: 1.2;
}
.site { display: flex; flex-direction: column; line-height: 1.2; }
.site__name { font-weight: 500; }
.site__id { font-size: 0.72rem; color: var(--ink-2); }

/* Table */
.sge-table {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 6px;
}
.sge-table :deep(th),
.sge-table :deep(th.v-data-table-column--align-end),
.sge-table :deep(th.v-data-table-column--align-start) {
  text-align: center !important;
}
.sge-table :deep(th .v-data-table-header__content) {
  justify-content: center;
  width: 100%;
}
.sge-table :deep(th) {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--ink);
  height: 44px;
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
  background: #faf8f4;
}
.sge-table :deep(thead tr:first-child th[colspan]) {
  border-bottom: 1px solid var(--line);
  font-size: 0.95rem;
  font-weight: 700;
}
.sge-table :deep(td) {
  font-size: 0.85rem;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  border-bottom: 1px solid #f0ece4 !important;
}
.sge-table :deep(tbody tr:hover) { background: #fbfaf7; }
.row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Buttons: sentence case, no tracking */
.sge-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}
.sge-row-btn {
  min-width: 104px;
  height: 32px;
}

/* Row whose PLC cannot be reached (disconnected or latency 0): the whole line is an alarm. */
.sge-table :deep(tr.sge-plc-down-row),
.sge-table :deep(tr.sge-plc-down-row:hover),
.sge-table :deep(tr.sge-plc-down-row > td),
.sge-table :deep(tr.sge-plc-down-row:hover > td) {
  background-color: #f6c4bf !important;
  color: #7a1410 !important;
}
.sge-table :deep(tr.sge-plc-down-row > td:first-child) {
  box-shadow: inset 6px 0 0 var(--stop);
}
.sge-table :deep(tr.sge-plc-down-row .site__name) {
  font-weight: 700;
}
.sge-table :deep(tr.sge-plc-down-row .sub),
.sge-table :deep(tr.sge-plc-down-row .site__id),
.sge-table :deep(tr.sge-plc-down-row .num),
.sge-table :deep(tr.sge-plc-down-row .state),
.sge-table :deep(tr.sge-plc-down-row .state--none),
.sge-table :deep(tr.sge-plc-down-row .state--run) {
  color: #7a1410 !important;
  opacity: 1;
}
.sge-table :deep(tr.sge-plc-down-row .state--bad) {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* Row whose last order is Stop: the park is deliberately stopped. Blue, so red stays for problems. */
.sge-table :deep(tr.sge-stop-row > td) {
  background-color: var(--halt-bg) !important;
  color: #063a66;
}
.sge-table :deep(tr.sge-stop-row:hover > td) {
  background-color: var(--halt-bg-strong) !important;
}
.sge-table :deep(tr.sge-stop-row > td:first-child) {
  box-shadow: inset 6px 0 0 var(--halt);
}
.sge-table :deep(tr.sge-stop-row .site__name) {
  color: var(--halt);
  font-weight: 700;
}
.sge-table :deep(tr.sge-stop-row .site__id),
.sge-table :deep(tr.sge-stop-row .sub),
.sge-table :deep(tr.sge-stop-row .num),
.sge-table :deep(tr.sge-stop-row .state--none) {
  color: #063a66;
  opacity: 0.85;
}
.sge-table :deep(tr.sge-stop-row .state--stop) {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* Confirmation dialog */
.confirm {
  --tone: var(--halt);
  --tone-bg: var(--halt-bg);
  border-radius: 10px !important;
  overflow: hidden;
}
.confirm--start {
  --tone: var(--run);
  --tone-bg: #e8f3e9;
}
.confirm__band {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  background: var(--tone-bg);
  border-bottom: 3px solid var(--tone);
  color: var(--tone);
}
.confirm__icon { color: var(--tone); }
.confirm__kicker {
  font-size: 0.75rem;
  color: var(--ink-2);
  line-height: 1.2;
}
.confirm__title {
  font-size: 1.2rem;
  font-weight: 600;
  line-height: 1.25;
  margin: 2px 0;
}
.confirm__sub {
  font-size: 0.85rem;
  color: var(--ink-2);
}
.confirm__body { padding: 18px 22px 8px !important; }
.confirm__sites {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 180px;
  overflow: auto;
  margin-bottom: 16px;
}
.confirm__site {
  padding: 3px 10px;
  border-radius: 999px;
  background: #f3f0ea;
  border: 1px solid var(--line);
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--ink);
}
.confirm__site--raised {
  border-color: var(--warn);
  color: var(--warn);
}
.confirm__site-min {
  font-weight: 400;
  opacity: 0.8;
  margin-left: 4px;
}
.limit {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  padding: 12px 14px;
  margin-bottom: 12px;
}
.limit__row {
  display: flex;
  align-items: center;
  gap: 16px;
}
.limit__text { flex: 1; min-width: 0; }
.limit__label {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.95rem;
}
.limit__hint {
  font-size: 0.8rem;
  color: var(--ink-2);
  line-height: 1.35;
  margin-top: 2px;
}
.limit__field {
  flex: none;
  width: 140px;
}
.limit__field :deep(input) {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.limit__info {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--line);
  font-size: 0.78rem;
  line-height: 1.4;
  color: var(--warn);
}
.option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  transition: border-color 150ms ease, background-color 150ms ease;
}
.option--on {
  border-color: var(--tone);
  background: var(--tone-bg);
}
.option__text { flex: 1; min-width: 0; }
.option__label {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.95rem;
}
.option--on .option__label { color: var(--tone); }
.option__hint {
  font-size: 0.8rem;
  color: var(--ink-2);
  line-height: 1.35;
  margin-top: 2px;
}
.option__switch { flex: none; }
.confirm__note {
  display: flex;
  align-items: flex-start;
  font-size: 0.78rem;
  color: var(--ink-2);
  margin: 14px 0 0;
  line-height: 1.35;
}
.confirm__actions { padding: 10px 16px 16px !important; }
.confirm__send { min-width: 180px; }

@media (prefers-reduced-motion: reduce) {
  .option { transition: none; }
}

/* Results dialog */
.results {
  --tone: var(--run);
  --tone-bg: #e8f3e9;
  border-radius: 10px !important;
  overflow: hidden;
}
.results--ko {
  --tone: var(--stop);
  --tone-bg: #fbeae8;
}
.results__band {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  background: var(--tone-bg);
  border-bottom: 3px solid var(--tone);
  color: var(--tone);
}
.results__icon { color: var(--tone); }
.results__kicker { font-size: 0.75rem; color: var(--ink-2); line-height: 1.2; }
.results__title { font-size: 1.2rem; font-weight: 600; line-height: 1.25; margin: 2px 0; }
.results__sub { font-size: 0.85rem; color: var(--ink-2); }
.results__body { padding: 8px 10px !important; }
.rlist { list-style: none; margin: 0; padding: 0; }
.rlist__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 6px;
}
.rlist__row + .rlist__row { margin-top: 2px; }
.rlist__row--ok .rlist__icon { color: var(--run); }
.rlist__row--ko { background: var(--stop-bg); }
.rlist__row--ko .rlist__icon,
.rlist__row--ko .rlist__site { color: var(--stop); }
.rlist__main { flex: 1; min-width: 0; }
.rlist__site { font-weight: 500; }
.rlist__err {
  font-size: 0.78rem;
  color: var(--stop);
  opacity: 0.9;
  line-height: 1.3;
  word-break: break-word;
}
.rlist__meta {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 0.82rem;
  color: var(--ink-2);
  flex: none;
}
.rlist__kw { color: var(--ink); font-weight: 500; }
.rlist__code { min-width: 2.4em; text-align: right; }
.rlist__ms { min-width: 5em; text-align: right; }
.results__actions { padding: 10px 16px 16px !important; }

/* Tooltips (unscoped content-class) */
:global(.sge-tip) {
  font-size: 0.8rem;
  line-height: 1.35;
}

@media (max-width: 900px) {
  .sge { padding: 8px 8px 16px; }
  .tools { width: 100%; flex-wrap: wrap; border-radius: 16px; }
  .tools__search { flex: 1; width: auto; min-width: 160px; }
  .fleet__alerts { margin-left: 0; }
  .confirm__sites { columns: 1; }
}
</style>
